import { Suspense, useEffect, useState } from "react";
import {
  TableFilters,
  TableFiltersState,
} from "../../../common/components/ui/table/TableFilters";
import {
  IRenter,
  RENTER_STATUS,
  FINANCIAL_STATUS,
  RenterType,
} from "../../../types/renter.type";
import { getRentersDelinquency } from "../../../common/services/delinquency.api";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import { getCompanyId } from "../../../common/services/company.api";
import Loading from "../../../common/components/ui/loading/Loading";
import GenericTable from "../../../common/components/ui/table/GenericTable";
import { formatDateAndTime } from "../../../common/helpers/date.helpers";
import TableFooter from "../../../common/components/ui/table/TableFooter";
// import { getRenters } from "../../../common/services/renters.api";
import { useNavigate } from "react-router-dom";
import { useCompanyStore } from "../../../common/states/store/Company";
import ModalChangeStatus from "../../renters/components/modals/ChangeStatusRenter";
import { getCompanySigla } from "@/common/helpers/company.helpers";
import ModalEditRenter from "@/modules/renters/components/modals/EditRenter";

// eslint-disable-next-line react-refresh/only-export-components
export const STATUS_LABELS = {
  [RENTER_STATUS.Recision]: "Recisão",
  [RENTER_STATUS.Finished]: "Finalizado",
  [RENTER_STATUS.Blacklist]: "Blacklist",
};

// eslint-disable-next-line react-refresh/only-export-components
export const SITUATION_FINANCIAL_LABELS = {
  [FINANCIAL_STATUS.EM_DIA]: "Em dia",
  [FINANCIAL_STATUS.AGUARDANDO_NEGATIVACAO]: "Aguardando Negativação",
  [FINANCIAL_STATUS.NEGATIVADO]: "Já Negativado",
  [FINANCIAL_STATUS.PARCIALMENTE_NEGATIVADO]: "Parcialmente Negativado",
  [FINANCIAL_STATUS.INDEFINIDO]: "Indefinido",
};

// eslint-disable-next-line react-refresh/only-export-components
export function getStatusLabelsRenters(status: RENTER_STATUS) {
  return STATUS_LABELS[status] || status;
}

const STATUS_COLORS = {
  [FINANCIAL_STATUS.EM_DIA]: "bg-greenPrimary text-white",
  [FINANCIAL_STATUS.AGUARDANDO_NEGATIVACAO]: "bg-yellowPrimary",
  [FINANCIAL_STATUS.NEGATIVADO]: "bg-redPrimary text-white",
  [FINANCIAL_STATUS.PARCIALMENTE_NEGATIVADO]: "bg-bluePrimary text-white",
  [FINANCIAL_STATUS.INDEFINIDO]: "bg-gray text-white",
};

// eslint-disable-next-line react-refresh/only-export-components
export function getSituationRenters(status: unknown) {
  const financialStatus = status as FINANCIAL_STATUS;
  return (
    <span
      className={`inline-block px-2 py-1 rounded-md text-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px] md:max-w-full ${STATUS_COLORS[financialStatus]}`}
      title={FINANCIAL_STATUS[financialStatus] || financialStatus}
    >
      {FINANCIAL_STATUS[financialStatus] || financialStatus}
    </span>
  );
}

const EMPTY_ACTION = { eventName: "", data: undefined };

function TableDelinqunecy() {
  const companyId = getCompanyId();
  const company = useCompanyStore();
  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [renters, setRenters] = useState<IRenter[]>([]);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    situation: "",
    situationFinancial: "",
    filterColumn: "",
    orderBy: "",
  });

  const headers = {
    companyId: "Cidade",
    name: "Nome",
    situationFinancial: "Situação do Cliente",
    status: "Status",
    updatedAt: "Atualizado em",
    createdAt: "Criado em",
  };

  useEffect(() => {
    async function loadRenters(filters, page) {
      setIsLoading(true);
      setRenters([]);

      const filtersCopy = { ...filters };

      if (filtersCopy.status.length === 0) {
        filtersCopy.status = [
          RENTER_STATUS.Recision,
          RENTER_STATUS.Finished,
          RENTER_STATUS.Bloqueado,
          RENTER_STATUS.Blacklist,
        ].join(",");
      }

      filtersCopy.orderBy = "situationFinancial";
      filtersCopy.situation = "";

      const result = await getRentersDelinquency({ ...filtersCopy, page });

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });

      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        return;
      }

      setRenters(result.data); // Armazena os dados com a nova propriedade
      setIsLoading(false);
    }

    loadRenters(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, companyId, company]);

  const actions = [
    {
      label: "Editar",
      action: (data) =>
        setAction({ eventName: "edit", data: (data as RenterType)._id }),
      permission: "edit:renters",
      // disabled: (data) => ![
      //   RENTER_STATUS.PendentePagamento,
      //   RENTER_STATUS.ProntoParaAprovacao,
      //   RENTER_STATUS.Incompleto,
      //   RENTER_STATUS.Indefinido,
      //   RENTER_STATUS.LeadQualificado,
      //   RENTER_STATUS.Ativo
      // ].includes((data as RenterType).status as RENTER_STATUS)
    },
    {
      label: "Trocar status",
      action: (data) => setAction({ eventName: "changeStatus", data: data }),
      disabled: (data) =>
        [RENTER_STATUS.Ativo].includes(
          (data as RenterType).status as RENTER_STATUS
        ),
      permission: "changeSatus:renters",
    },
  ];

  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = () => {
    handleSearch(filters);
    setAction(EMPTY_ACTION);
  };

  const handleSearch = ({ search, status }) => {
    const newState = Object.assign({}, filters, { search, status });
    setFilters(newState);
  };

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Clientes finalizados"
          placeholder="Digite o nome, o tipo ou o e-mail."
          statusOptions={STATUS_LABELS}
          situationOptions={SITUATION_FINANCIAL_LABELS}
          newItemLabel="Novo Colaborador"
          onNewItemClick={() => { }}
          onSearch={handleFilterChange}
          entity="delinquency"
          tableData={renters}
        />

        <GenericTable
          headers={headers}
          data={renters}
          actions={actions}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={{
            companyId: (companyId) => {
              return getCompanySigla(companyId as string);
            },
            name: (name, renter) => (
              <a
                href={`/finalizados/${(renter as IRenter)._id}`}
                onClick={(e) => {
                  e.preventDefault(); // Para evitar o comportamento padrão do link
                  navigate(`/finalizados/${(renter as IRenter)._id}`); // Navegação na mesma página
                }}
                target="_blank" // Abre em uma nova aba
                rel="noopener noreferrer" // Melhoria de segurança
              >
                {name as string}
              </a>
            ),
            updatedAt: formatDateAndTime,
            createdAt: formatDateAndTime,
            situationFinancial: getSituationRenters,
            status: (data: unknown) =>
              getStatusLabelsRenters(data as RENTER_STATUS),
          }}
          entity="delinquency"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
        {isAction("changeStatus") && (
          <ModalChangeStatus
            renter={action.data as IRenter}
            onClose={cleanActionData}
          />
        )}
        {isAction("edit") && (
          <ModalEditRenter
            renterId={action.data as string}
            onClose={cleanActionData}
          />
        )}
      </div>
    </Suspense>
  );
}

export default TableDelinqunecy;
